import { Task } from "@lit/task";
import {
    BillingMode,
    type Company,
    CompanyLifeCycleEvent,
    CompanyStatus,
    companyStatusColor,
    companyStatusLabel,
    DeleteDraftInvoiceParams,
    DeletePaymentMethodParams,
    type Employee,
    EmployeeStatus,
    employeeStatusColor,
    employeeStatusLabel,
    ExportCompanyParams,
    Feature,
    formatDate,
    formatNumber,
    GetCompanyParams,
    GetInvoicesParams,
    HOGAST_UNIONS,
    type HogastUnionId,
    type ImportCoreDataResponse,
    type Invoice,
    InvoiceStatus,
    NotifyYnaEnabledParams,
    PartnerIntegration,
    Role,
    SendInvoiceParams,
    SendOwnerWelcomeEmailDTO,
    serialize,
    sortLifeCycleEvents,
    toDateString,
    ToggleBetaCustomerParams,
    UpdateBillingParams,
    UpdateCompanyParams,
    UpdateEmployeeParams,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import "@pentacode/ui/components";
import { css, html, LitElement, type PropertyValues } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import "../../components/avatar/avatar";
import { Textarea } from "../../components/text-area/textarea";
import { loadEnabledFeatures } from "../../contexts/features-context";
import { PUBLIC_PTC_LOAD_INVOICES_ADMIN, PUBLIC_PTC_MANAGE_URL } from "../../env";
import { app, router } from "../../init";
import { singleton } from "../../lib/singleton";
import { decodeText, saveFile, selectFile } from "../../lib/util";
import { config, shared } from "../../styles";
import { alert, confirm } from "../alert-dialog";
import "../billing-dialog";
import type { BillingDialog } from "../billing-dialog";
import { Checkbox } from "../checkbox";
import { DateInput } from "../date-input";
import "../invoice-dialog";
import type { InvoiceDialog } from "../invoice-dialog";
import "../popover";
import "../scroller";
import type { Scroller } from "../scroller";
import "./beta-toggle-dialog";
import type { BetaToggleDialog } from "./beta-toggle-dialog";
import "./login-throttle-dialog";
import type { LoginThrottleDialog } from "./login-throttle-dialog";
import "./restore-time-entries-dialog";
import type { RestoreTimeEntriesDialog } from "./restore-time-entries-dialog";
import "./unlock-revenue-entries-dialog";
import type { UnlockRevenueEntriesDialog } from "./unlock-revenue-entries-dialog";
import "./yna-enable-dialog";
import type { YnaEnableDialog } from "./yna-enable-dialog";

@customElement("ptc-admin-company")
export class AdminCompany extends LitElement {
    @property({ type: Number })
    companyId: number | null = null;

    @state()
    private _company: Company | null = null;

    @query("#billingForm input[name='email']")
    private _billingEmailInput: HTMLInputElement;

    @state()
    private _loading = false;

    @state()
    private _displayAllEmployees = false;

    @state()
    private _importResult: ImportCoreDataResponse | null = null;

    @singleton("ptc-billing-dialog", true)
    private _billingDialog: BillingDialog;

    @singleton("ptc-beta-toggle-dialog", true)
    private _betaToggleDialog: BetaToggleDialog;

    @singleton("ptc-login-throttle-dialog")
    private _loginThrottleDialog: LoginThrottleDialog;

    @singleton("ptc-yna-enable-dialog", true)
    private _ynaEnableDialog: YnaEnableDialog;

    @singleton("ptc-restore-time-entries-dialog")
    private _restoreTimeEntriesDialog: RestoreTimeEntriesDialog;

    @singleton("ptc-unlock-revenue-entries-dialog")
    private _unlockRevenueEntriesDialog: UnlockRevenueEntriesDialog;

    @query(".new-event-form")
    private _newEventForm: HTMLFormElement;

    @query("#timeLineScroller")
    private _timeLineScroller: Scroller;

    @singleton("ptc-invoice-dialog")
    private _invoiceDialog: InvoiceDialog;

    private _invoicesTask = new Task(this, {
        task: async ([companyId, api, ignoreCache]) => {
            if (!companyId) {
                return [];
            }

            return api.getInvoices(
                new GetInvoicesParams({ id: companyId, includeDrafts: true, includeVoided: true, ignoreCache })
            );
        },
        args: () => [this.companyId, app.api, Boolean(false)] as const,
        autoRun: false,
    });

    protected update(changedProperties: PropertyValues): void {
        super.update(changedProperties);

        // run the task manually instead of in autoRun mode, to avoid unnecessary calls after fetching without cache
        if (PUBLIC_PTC_LOAD_INVOICES_ADMIN === "true" && changedProperties.has("companyId")) {
            void this._invoicesTask.run();
        }
    }

    private get _companyInfo() {
        const form = this.renderRoot!.querySelector("#companyForm") as HTMLFormElement;
        const data = form && new FormData(form);
        return (
            (data && {
                id: parseInt(data.get("id") as string),
                name: data.get("name") as string,
                address: data.get("address") as string,
                postalCode: data.get("postalCode") as string,
                city: data.get("city") as string,
                phone: data.get("phone") as string,
                email: data.get("email") as string,
                features: data.getAll("features") as Feature[],
                partnerIntegrations: data.getAll("partnerIntegrations") as PartnerIntegration[],
                comments: data.get("comments") as string,
                startLedgers: data.get("startLedgers") as string,
            }) ||
            null
        );
    }

    private get _billingInfo() {
        const form = this.renderRoot!.querySelector("#billingForm") as HTMLFormElement;
        const data = form && new FormData(form);
        const billingMode = data?.get("billingMode") as BillingMode;

        const numberOfEmployeesRaw = data?.get("numberOfEmployees")?.toString();
        const numberOfEmployees =
            numberOfEmployeesRaw && billingMode === BillingMode.Yearly ? parseInt(numberOfEmployeesRaw) : null;
        return (
            (data && {
                billingMode,
                hogastUnionId: (data.get("hogastUnionId") as HogastUnionId) || null,
                hogastMemberId: (data.get("hogastMemberId") as string) || null,
                name: data.get("name") as string,
                address: data.get("address") as string,
                addressLine1: data.get("addressLine1") as string,
                addressLine2: data.get("addressLine2") as string,
                postalCode: data.get("postalCode") as string,
                city: data.get("city") as string,
                phone: data.get("phone") as string,
                email: billingMode === BillingMode.Hogast ? "hogast@pentacode.app" : (data.get("email") as string),
                vatId: data.get("vatId") as string,
                comments: data.get("comments") as string,
                numberOfEmployees,
            }) ||
            null
        );
    }

    private get _companyInfoChanged() {
        const before = this._company;
        const after = this._companyInfo;

        return (
            before &&
            after &&
            before.id === after.id &&
            // TODO: we should just use deepEqual here
            (before.name !== after.name ||
                before.address !== after.address ||
                before.postalCode !== after.postalCode ||
                before.city !== after.city ||
                before.phone !== after.phone ||
                before.email !== after.email ||
                before.comments !== after.comments ||
                before.features.length !== after.features.length ||
                after.features.some((f) => !before.features.includes(f)) ||
                before.partnerIntegrations.length !== after.partnerIntegrations.length ||
                after.partnerIntegrations.some((f) => !before.partnerIntegrations.includes(f)))
        );
    }

    private get _billingInfoChanged() {
        const before = this._company?.billing?.info;
        const after = this._billingInfo;

        const billingModeYearlyUnchanged =
            after?.billingMode === BillingMode.Yearly && before?.billingMode === BillingMode.Yearly;

        return (
            after &&
            (!before ||
                before.billingMode !== after.billingMode ||
                before.hogastUnionId !== after.hogastUnionId ||
                before.hogastMemberId !== after.hogastMemberId ||
                before.name !== after.name ||
                before.addressLine1 !== after.addressLine1 ||
                before.addressLine2 !== after.addressLine2 ||
                before.postalCode !== after.postalCode ||
                before.city !== after.city ||
                before.phone !== after.phone ||
                before.email !== after.email ||
                before.comments !== after.comments ||
                (billingModeYearlyUnchanged && before.numberOfEmployees !== after.numberOfEmployees))
        );
    }

    private get _newEventData(): Partial<CompanyLifeCycleEvent> {
        const form = this._newEventForm;
        const data = form && new FormData(form);
        return data
            ? {
                  comment: data.get("comment") as string,
                  status: (data.get("status") as CompanyStatus) || undefined,
                  date: (data.get("date") as DateString) || toDateString(new Date()),
              }
            : { comment: "" };
    }

    async updated(changes: Map<string, unknown>) {
        if (changes.has("companyId")) {
            this._importResult = null;
            this._displayAllEmployees = false;
            await this._load();
            await this.updateComplete;

            if (this._timeLineScroller) {
                this._timeLineScroller.scrollTop = this._timeLineScroller.scrollHeight;
            }
        } else if (changes.has("_company")) {
            await this._resetForms();
        }
    }

    private async _deleteDraftInvoice(invoice: Invoice) {
        if (
            !(await confirm(
                "Willst du diesen Rechnungsentwurf wirklich löschen? Der Entwurf wird auch bei Stripe gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.",
                "Löschen",
                "Abbrechen",
                {
                    title: "Rechnungsentwurf löschen",
                    type: "destructive",
                }
            ))
        ) {
            return;
        }

        if (!this._company || !this._company.billing) {
            return;
        }

        await app.api.deleteDraftInvoice(new DeleteDraftInvoiceParams({ companyId: this._company.id, id: invoice.id }));

        await this._load();
    }

    private async _sendInvoice(invoiceId: string) {
        if (
            !(await confirm("Willst du diese Rechnung jetzt senden?", "Senden", "Abbrechen", {
                title: "Rechnung erneut versenden",
                type: "question",
            }))
        ) {
            return;
        }

        if (!this._company || !this._company.billing) {
            return;
        }

        await app.api.sendInvoice(new SendInvoiceParams({ invoiceId }));

        await this._invoicesTask.run([this._company.id, app.api, true]);
    }

    private async _resetForms() {
        await this.updateComplete;
        this._resetCompanyForm();
        this._resetBillingForm();
        setTimeout(() => this.requestUpdate(), 100);
    }

    private _resetCompanyForm() {
        (["name", "address", "postalCode", "city", "phone", "email", "comments", "country"] as const).forEach(
            (prop) => {
                const input = this.renderRoot.querySelector(`#companyForm [name="${prop}"]`) as HTMLInputElement;
                input.value = this._company?.[prop] || "";
            }
        );
        for (const input of this.renderRoot.querySelectorAll(
            `#companyForm [name="features"]`
        ) as NodeListOf<Checkbox>) {
            input.checked = Boolean(this._company?.features.includes(input.value as Feature));
        }

        for (const input of this.renderRoot.querySelectorAll(
            `#companyForm [name="partnerIntegrations"]`
        ) as NodeListOf<Checkbox>) {
            input.checked = Boolean(this._company?.partnerIntegrations.includes(input.value as PartnerIntegration));
        }
    }

    private _resetBillingForm() {
        (
            [
                "name",
                "addressLine1",
                "addressLine2",
                "postalCode",
                "city",
                "phone",
                "email",
                "comments",
                "vatId",
                "billingMode",
                "hogastUnionId",
                "hogastMemberId",
                "numberOfEmployees",
            ] as const
        ).forEach((prop) => {
            const input = this.renderRoot.querySelector(`#billingForm [name="${prop}"]`) as HTMLInputElement;
            input.value = this._company?.billing?.info[prop]?.toString() || "";
        });
    }

    private async _load() {
        if (!this.companyId) {
            this._company = null;
            return;
        }
        this._loading = true;
        try {
            this._company = await app.api.getCompany(new GetCompanyParams({ id: this.companyId, forceReload: true }));
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _submitUpdateCompanyInfo(e: FocusEvent) {
        e.preventDefault();

        if (!this._companyInfo || !this._company) {
            return;
        }

        const ynaNewlyEnabled =
            this._companyInfo.features.includes(Feature.voiceControlAi) &&
            !this._company.features.includes(Feature.voiceControlAi);

        let notifyYnaResult: { notifyOwner: boolean; notifyManagers: boolean } | null = null;

        if (ynaNewlyEnabled && loadEnabledFeatures(this._company.isBetaCustomer).ynaEnableNotification) {
            const result = await this._ynaEnableDialog.show({
                name: this._company.name,
                effectiveCompanyStatus: this._company.effectiveStatus,
            });
            if (!result?.confirmed) {
                return;
            }
            notifyYnaResult = { notifyOwner: result.notifyOwner, notifyManagers: result.notifyManagers };
        }

        this._loading = true;
        try {
            await app.api.updateCompany(new UpdateCompanyParams(this._companyInfo));

            if (notifyYnaResult) {
                await app.api.notifyYnaEnabled(
                    new NotifyYnaEnabledParams({
                        companyId: this._company.id,
                        notifyOwner: notifyYnaResult.notifyOwner,
                        notifyManagers: notifyYnaResult.notifyManagers,
                    })
                );
            }

            await this._load();
            this.dispatchEvent(new CustomEvent("updated"));
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _submitUpdateBillingInfo(e: FocusEvent) {
        e.preventDefault();

        if (!this._billingInfo || !this._company) {
            return;
        }

        this._loading = true;
        try {
            const {
                name,
                email,
                addressLine1,
                addressLine2,
                city,
                postalCode,
                phone,
                vatId,
                comments,
                billingMode,
                hogastMemberId,
                hogastUnionId,
                numberOfEmployees,
            } = this._billingInfo;
            await app.api.updateCompany(
                new UpdateCompanyParams({
                    id: this._company.id,
                    billing: new UpdateBillingParams({
                        name,
                        email,
                        addressLine1,
                        addressLine2,
                        city,
                        postalCode,
                        phone,
                        vatId,
                        comments,
                        billingMode,
                        hogastMemberId,
                        hogastUnionId,
                        numberOfEmployees,
                    }),
                })
            );
            this.dispatchEvent(new CustomEvent("updated"));
            await this._load();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _addPaymentMethod(company: Company) {
        const complete = await this._billingDialog.show(company);

        if (complete) {
            this.dispatchEvent(new CustomEvent("updated"));
            void alert("Zahungsmittel erfolgreich hinzugefügt!");
            this._loading = true;
            await this._load();
            this._loading = false;
        }
    }

    private async _deletePaymentMethod(paymentMethodId: string) {
        if (!this._company?.billing) {
            return;
        }

        if (
            !(await confirm("Willst du dieses Zahlungsmittel wirklich löschen?", "Löschen", "Abbrechen", {
                title: "Zahlungsmittel Löschen",
                type: "destructive",
            }))
        ) {
            return;
        }

        this._loading = true;
        try {
            await app.api.deletePaymentMethod(
                new DeletePaymentMethodParams({ companyId: this._company.id, paymentMethodId })
            );
            await this._load();
            this.dispatchEvent(new CustomEvent("updated"));
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private _openStripeDashboard(company: Company) {
        if (!company.billing) {
            return;
        }

        window.open(
            `https://dashboard.stripe.com${
                company.billing.customer && company.billing.customer.livemode ? "" : "/test"
            }/customers/${company.billing.customerId}`,
            "_blank"
        );
    }

    private async _submitNewEvent(e: Event) {
        e.preventDefault();

        this._loading = true;
        try {
            await app.api.updateCompany(
                new UpdateCompanyParams({
                    id: this._company!.id,
                    newLifeCycleEvent: new CompanyLifeCycleEvent(this._newEventData),
                })
            );
            await this._load();
            this.dispatchEvent(new CustomEvent("updated"));
            this._clearNewEventForm();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private _clearNewEventForm() {
        this.renderRoot
            .querySelectorAll(
                `.new-event-form [name="comment"], .new-event-form [name="status"], .new-event-form [name="date"]`
            )
            .forEach((input: HTMLInputElement | HTMLSelectElement) => (input.value = ""));
    }

    private async _deleteEvent(event: CompanyLifeCycleEvent) {
        if (
            !(await confirm("Willst du diesen Eintrag wirklich löschen?", "Löschen", "Abbrechen", {
                title: "Eintrag Löschen",
                type: "destructive",
            }))
        ) {
            return;
        }

        this._loading = true;
        try {
            await app.api.updateCompany(
                new UpdateCompanyParams({
                    id: this._company!.id,
                    deleteLifeCycleEvent: event.id,
                })
            );
            await this._load();
            this.dispatchEvent(new CustomEvent("updated"));
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _resendOwnerWelcomeEmail(owner: Employee) {
        if (
            !this._company ||
            !(await confirm(
                `Die Willkommens-E-Mail wird an ${owner.email} gesendet. Hat der Besitzer noch kein Passwort festgelegt, enthält sie einen neuen Link zum Festlegen des Passworts und der bisherige Link wird ungültig.`,
                "Senden",
                "Abbrechen",
                { title: "Willkommens-E-Mail erneut senden?" }
            ))
        ) {
            return;
        }

        this._loading = true;

        try {
            await app.api.sendOwnerWelcomeEmail(new SendOwnerWelcomeEmailDTO({ companyId: this._company.id }));
            await this._load();
            void alert(`Die Willkommens-E-Mail wurde an ${owner.email} gesendet.`, { type: "success" });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _unlockRevenueEntries() {
        if (!this._company) {
            return;
        }

        await this._unlockRevenueEntriesDialog.show({
            companyId: this._company.id,
        });
    }

    private async _restoreDeletedTimeEntries() {
        if (!this._company) {
            return;
        }

        await this._restoreTimeEntriesDialog.show({
            companyId: this._company.id,
            employees: [...this._company.employees],
        });
    }

    private async _restoreVenue(id: number, name: string) {
        if (!this._company) {
            return;
        }

        if (
            !(await confirm(`Willst du den Standort '${name}' wiederherstellen?`, "Wiederherstellen", "Abbrechen", {
                title: "Standort wiederherstellen",
                type: "question",
            }))
        ) {
            return;
        }

        try {
            await app.api.restoreVenue(id);
            void alert("Der Standort wurde erfolgreich wiederhergestellt.", { type: "success" });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        await this._load();
    }

    private async _toggleBetaCustomer() {
        if (!this._company) {
            return;
        }

        const result = await this._betaToggleDialog.show({
            isBetaCustomer: this._company.isBetaCustomer,
            name: this._company.name,
            effectiveCompanyStatus: this._company.effectiveStatus,
        });
        if (!result?.confirmed) {
            return;
        }

        const newBetaStatus = !this._company.isBetaCustomer;
        this._loading = true;

        try {
            await app.api.toggleBetaCustomer(
                new ToggleBetaCustomerParams({
                    companyId: this._company.id,
                    isBetaCustomer: newBetaStatus,
                    notifyOwner: result.notifyOwner,
                    notifyManagers: result.notifyManagers,
                })
            );
            await this._load();
            this.dispatchEvent(new CustomEvent("updated"));

            const statusText = newBetaStatus ? "Beta-Kunde aktiviert" : "Beta-Status entfernt";
            void alert(statusText, { type: "success" });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    // Not rendered while deleting companies is disabled on the server; kept for re-enabling with soft delete.
    private async _deleteCompany() {
        if (!this._company) {
            return;
        }

        if (
            !(await confirm(
                `Sind Sie sicher dass Sie das Unternehmen ${this._company.name} (ID: ${this._company.id}) löschen möchten?`,
                "Löschen",
                "Abbrechen",
                { type: "destructive" }
            ))
        ) {
            return;
        }

        this._loading = true;

        try {
            await app.api.deleteCompany(this._company.id);
            router.go("companies");
            this.companyId = this._company = null;
            this.dispatchEvent(new CustomEvent("updated"));
            await this._load();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _setContactPerson(employee: Employee, isContactPerson: boolean) {
        if (!this._company) {
            return;
        }

        this._loading = true;

        try {
            await app.api.updateEmployee(
                new UpdateEmployeeParams({
                    id: employee.id,
                    isContactPerson,
                })
            );
            await this._load();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _export() {
        this._loading = true;
        try {
            const exp = await app.api.exportCompany(new ExportCompanyParams({ companyId: this._company!.id }));
            saveFile(
                `Export_${this._company!.name}_${new Date().toISOString()}.json`,
                "application/json",
                JSON.stringify(serialize(exp))
            );
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _import() {
        try {
            const file = await selectFile();
            this._importResult = null;
            if (file) {
                this._loading = true;
                this._importResult = await app.api.importCoreData({
                    fileContent: decodeText(await file.arrayBuffer()),
                    companyId: this.companyId!,
                });
                await this._load();
                await this.updateComplete;
            }
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    static styles = [
        config,
        shared,
        Checkbox.styles,
        DateInput.styles,
        Textarea.styles,
        css`
            :host {
                position: relative;
            }

            input[readonly] {
                border: none;
            }

            .payment-method {
                position: relative;
            }

            .payment-method-container {
                border-radius: var(--border-radius);
                margin-bottom: 1em;
                padding: 0.5em 0.5em 0.5em 0.5em;
                border: solid 1px var(--shade-2);
                display: flex;
                height: auto;
                align-items: start;
                justify-content: space-between;
            }

            .payment-method-detail {
                display: flex;
                align-items: center;
                margin: 0.5em 0;
            }

            .payment-method-detail-label {
                font-weight: bold;
                width: 8em;
                text-align: right;
                margin-right: 1em;
            }

            .new-event-form textarea {
                padding-bottom: 3em;
            }

            .lifecycle-event:not(:hover) .delete-event-button {
                opacity: 0;
            }

            .lifecycle-event.future:not(:hover) {
                opacity: 0.5;
            }
        `,
    ];

    private _renderCompanyForm(company: Company) {
        return html`
            <form id="companyForm" @input=${() => this.requestUpdate()} @submit=${this._submitUpdateCompanyInfo}>
                <div class="spacing grid">
                    <div class="field">
                        <label>ID</label>
                        <input readonly name="id" .value=${live(company.id.toString())} />
                    </div>

                    <div class="field">
                        <label>Erstellungsdatum</label>
                        <input readonly .value=${live(formatDate(company.created))} />
                    </div>

                    <div class="field">
                        <label>Status</label>
                        <div class="horizontal layout spacing">
                            <div class="pill" style="--color-highlight: ${companyStatusColor(company.effectiveStatus)}">
                                ${companyStatusLabel(company.effectiveStatus)}
                            </div>
                            <div
                                class="pill click"
                                style="--color-highlight: ${company.isBetaCustomer ? "purple" : "var(--shade-3)"}"
                                @click=${this._toggleBetaCustomer}
                            >
                                Beta
                            </div>
                        </div>
                    </div>
                </div>

                <div class="field">
                    <label>Unternehmensname</label>
                    <input name="name" placeholder="Unternehmensname" />
                </div>

                <div class="field">
                    <label>Adresse</label>
                    <input type="text" name="address" placeholder="Strasse / Hausnummer" />
                </div>

                <div class="spacing grid">
                    <div class="field">
                        <label>Postleitzahl</label>
                        <input type="number" pattern="d*" name="postalCode" placeholder="PLZ" />
                    </div>

                    <div class="field">
                        <label>Stadt</label>
                        <input type="text" name="city" placeholder="Stadt" />
                    </div>

                    <div class="field">
                        <label>Land</label>
                        <select name="country" disabled>
                            <option value="DE">Deutschland</option>
                            <option value="AT">Österreich</option>
                        </select>
                    </div>
                </div>

                <div class="spacing grid">
                    <div class="field">
                        <label>Telefon</label>
                        <input type="text" name="phone" placeholder="Telefon" />
                    </div>

                    <div class="field">
                        <label>Emailadresse</label>
                        <input type="email" name="email" placeholder="Email" />
                    </div>
                </div>

                <div class="field">
                    <label>Bemerkungen</label>
                    <textarea name="comments"></textarea>
                </div>

                <div class="spacing grid">
                    <div class="field">
                        <label>Features</label>
                        <div class="small spacing grid">
                            <ptc-checkbox-button
                                name="features"
                                .value=${Feature.Employees}
                                label="Mitarbeiter"
                                buttonClass="slim ghost"
                                disabled
                            ></ptc-checkbox-button>
                            <ptc-checkbox-button
                                name="features"
                                .value=${Feature.Roster}
                                label="Dienstplan"
                                buttonClass="slim ghost"
                                disabled
                            ></ptc-checkbox-button>
                            <ptc-checkbox-button
                                name="features"
                                .value=${Feature.Time}
                                label="Stempeluhr"
                                buttonClass="slim ghost"
                            ></ptc-checkbox-button>
                            <ptc-checkbox-button
                                name="features"
                                .value=${Feature.Revenues}
                                label="Umsätze"
                                buttonClass="slim ghost"
                            ></ptc-checkbox-button>
                            <ptc-checkbox-button
                                name="features"
                                .value=${Feature.Planning}
                                label="Planung"
                                buttonClass="slim ghost"
                            ></ptc-checkbox-button>
                            <ptc-checkbox-button
                                name="features"
                                .value=${Feature.StaffApp}
                                label="Mitarbeiter-App"
                                buttonClass="slim ghost"
                            ></ptc-checkbox-button>
                            <ptc-checkbox-button
                                name="features"
                                .value=${Feature.voiceControlAi}
                                label="Yna"
                                buttonClass="slim ghost"
                            ></ptc-checkbox-button>
                            <ptc-checkbox-button
                                name="features"
                                .value=${Feature.PentacodePayroll}
                                label="Payroll"
                                buttonClass="slim ghost"
                                disabled
                            ></ptc-checkbox-button>
                        </div>
                    </div>
                </div>

                <div class="spacing grid">
                    <div class="field">
                        <label>Partner Integrationen</label>
                        <div class="small spacing grid">
                            <ptc-checkbox-button
                                name="partnerIntegrations"
                                .value=${PartnerIntegration.HorecaHero}
                                label="Horeca Hero"
                                buttonClass="slim ghost"
                            ></ptc-checkbox-button>
                            <ptc-checkbox-button
                                name="partnerIntegrations"
                                .value=${PartnerIntegration.Intratool}
                                label="Intratl: Nur Messe!"
                                buttonClass="slim ghost"
                            ></ptc-checkbox-button>
                            <ptc-checkbox-button
                                name="partnerIntegrations"
                                .value=${PartnerIntegration.Onboard}
                                label="Onbrd: Nur Messe!"
                                buttonClass="slim ghost"
                            ></ptc-checkbox-button>
                            <ptc-checkbox-button
                                name="partnerIntegrations"
                                .value=${PartnerIntegration.Uberblick}
                                label="Übrblck: Nur Messe!"
                                buttonClass="slim ghost"
                            ></ptc-checkbox-button>
                        </div>
                    </div>
                </div>

                <div class="horizontal spacing evenly stretching layout" ?hidden=${!this._companyInfoChanged}>
                    <button class="primary">Speichern</button>
                    <button
                        class="transparent"
                        type="button"
                        @click=${() => {
                            this._resetCompanyForm();
                            this.requestUpdate();
                        }}
                    >
                        Abbrechen
                    </button>
                </div>
            </form>
        `;
    }

    private _renderBillingForm() {
        return html`
            <form id="billingForm" @input=${() => this.requestUpdate()} @submit=${this._submitUpdateBillingInfo}>
                <div class="double-padded border-top">
                    <div class="margined uppercase">Abrechnung</div>

                    <div class="fields spacing grid" style="grid-template-columns: repeat(2, minmax(0, 1fr))">
                        <div class="field">
                            <label>Abrechnungsmodus</label>
                            <select
                                name="billingMode"
                                @change=${(e: Event) => {
                                    if ((e.target as HTMLSelectElement).value === BillingMode.Hogast) {
                                        this._billingEmailInput!.value = "hogast@pentacode.app";
                                    } else {
                                        this._billingEmailInput!.value = "";
                                    }
                                }}
                            >
                                <option .value=${BillingMode.Default}>Standard</option>
                                <option .value=${BillingMode.Hogast}>Hogast</option>
                                <option .value=${BillingMode.Legacy}>Alt</option>
                                <option .value=${BillingMode.Yearly}>Jahreslizenz</option>
                                <option .value=${BillingMode.Lifetime}>Lifetime-Lizenz</option>
                            </select>
                        </div>
                        <div class="field" ?hidden=${this._billingInfo?.billingMode !== BillingMode.Hogast}>
                            <label>Hogast Dachverband</label>
                            <select
                                name="hogastUnionId"
                                ?required=${this._billingInfo?.billingMode === BillingMode.Hogast}
                            >
                                ${HOGAST_UNIONS.map(
                                    (union) => html` <option .value=${union.id}>${union.name}</option> `
                                )}
                            </select>
                        </div>
                        <div class="field" ?hidden=${this._billingInfo?.billingMode !== BillingMode.Hogast}>
                            <label>Hogast Mitgliedsnummer</label>
                            <input
                                name="hogastMemberId"
                                ?required=${this._billingInfo?.billingMode === BillingMode.Hogast}
                            />
                        </div>
                        <div class="field" ?hidden=${this._billingInfo?.billingMode !== BillingMode.Yearly}>
                            <label>Vereinbarte Anzahl der Mitarbeiter</label>
                            <input
                                name="numberOfEmployees"
                                type="number"
                                ?required=${this._billingInfo?.billingMode === BillingMode.Yearly}
                            />
                        </div>
                        <div
                            class="field"
                            title="Mindestanzahl an Mitarbeitern, die pro Abrechnungsperiode berechnet wird, unabhängig von der tatsächlichen Mitarbeiteranzahl."
                        >
                            <label>Abrechnung min. Mitarbeiter</label>
                            <input
                                readonly
                                style="cursor: not-allowed"
                                .value=${live((this._company?.billing?.minNumOfBillableEmployees ?? "").toString())}
                            />
                        </div>
                    </div>
                </div>

                <div class="double-padded border-top">
                    <div class="margined uppercase">Rechnungsanschrift</div>

                    <div class="field">
                        <label>Name</label>
                        <input name="name" placeholder="Name" />
                    </div>

                    <div class="field">
                        <label>Adresse</label>
                        <div class="spacing vertical layout">
                            <input type="text" name="addressLine1" placeholder="Strasse / Hausnummer (1. Zeile)" />
                            <input type="text" name="addressLine2" placeholder="Strasse / Hausnummer (2. Zeile)" />
                        </div>
                    </div>

                    <div class="spacing grid">
                        <div class="field">
                            <label>Postleitzahl</label>
                            <input type="number" pattern="d*" name="postalCode" placeholder="PLZ" />
                        </div>

                        <div class="field">
                            <label>Stadt</label>
                            <input type="text" name="city" placeholder="Stadt" />
                        </div>
                    </div>

                    <div class="spacing grid">
                        <div class="field stretch">
                            <label>Telefon</label>
                            <input type="text" name="phone" placeholder="Telefon" />
                        </div>

                        <div class="field stretch">
                            <label>Emailadresse</label>
                            <input
                                type="email"
                                name="email"
                                placeholder="Email"
                                ?disabled=${this._billingInfo?.billingMode === BillingMode.Hogast}
                            />
                        </div>
                    </div>

                    <div class="field">
                        <label>USt-ID</label>
                        <input type="text" name="vatId" />
                    </div>

                    <div class="field">
                        <label>Kommentare</label>
                        <ptc-textarea name="comments"></ptc-textarea>
                    </div>
                </div>

                <div
                    class="double-margined horizontal spacing evenly stretching layout"
                    ?hidden=${!this._billingInfoChanged}
                >
                    <button class="primary">Speichern</button>
                    <button
                        class="transparent"
                        type="button"
                        @click=${() => {
                            this._resetBillingForm();
                            this.requestUpdate();
                        }}
                    >
                        Abbrechen
                    </button>
                </div>
            </form>
        `;
    }

    private _renderLifeCycleEvents() {
        if (!this._company) {
            return;
        }

        const events = sortLifeCycleEvents(this._company.lifeCycleEvents);
        let status: CompanyStatus | undefined;

        return html`
            ${events.map((event) => {
                const prevStatus = status;
                if (event.status) {
                    status = event.status;
                }
                return html`
                    <div
                        class="lifecycle-event blue ${event.date > toDateString(new Date()) ? "future" : ""}"
                        style="border-left: dashed 1px var(--shade-3); margin: 0 1em; --color-highlight: ${companyStatusColor(
                            status
                        )}"
                    >
                        <div
                            class="horizontal spacing center-aligning layout"
                            style="margin-left: -0.5em; padding-top: 1em;"
                        >
                            <div
                                style="width: 1em; height: 1em; border-radius: 100%; background: var(--color-highlight);"
                            ></div>
                            <div class="small stretch bold">${formatDate(event.date)}</div>
                            <button
                                class="tiny red skinny transparent delete-event-button"
                                @click=${() => this._deleteEvent(event)}
                            >
                                <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                            </button>
                            <div class="small subtle">${event.createdBy}</div>
                        </div>
                        <div class="padded horizontally-margined">${event.comment}</div>
                        ${prevStatus && prevStatus !== status
                            ? html`
                                  <div
                                      class="tiny horizontally-padded horizontally-margined horizontal spacing center-aligning layout"
                                  >
                                      <div class="pill" style="--color-highlight: ${companyStatusColor(prevStatus)}">
                                          ${companyStatusLabel(prevStatus)}
                                      </div>
                                      <ptc-ui-icon name="icon.navigation.forward"></ptc-ui-icon>
                                      <div class="pill" style="--color-highlight: ${companyStatusColor(status)}">
                                          ${companyStatusLabel(status)}
                                      </div>
                                  </div>
                              `
                            : ""}
                    </div>
                `;
            })}
        `;
    }

    render() {
        const company = this._company;

        if (!company) {
            return html`
                <div class="fullbleed centering vertical subtle layout">
                    <ptc-ui-icon name="icon.object.company" class="giant"></ptc-ui-icon>
                    <div>Kein Unternehmen gewählt.</div>
                </div>
            `;
        }

        const activeEmployees = company.employees.filter((e) => e.active);

        const statusOrder = [EmployeeStatus.Active, EmployeeStatus.Probation, EmployeeStatus.Retired];

        const employees = company.employees
            .filter((e) => e.role < Role.Worker || e.isContactPerson || (this._displayAllEmployees && e.active))
            .sort(
                (a, b) =>
                    a.role - b.role ||
                    Number(b.isContactPerson) - Number(a.isContactPerson) ||
                    statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status)
            );

        return html`
            <div class="fullbleed horizontal layout">
                <div class="vertical layout stretch collapse">
                    <div class="padded center-aligning spacing horizontal layout">
                        <div class="stretch"></div>
                        <button class="small slim ghost" @click=${this._export}>
                            <ptc-ui-icon name="icon.action.export"></ptc-ui-icon>
                            Exportieren
                        </button>
                        <a
                            class="small slim ghost button"
                            href="https://dashboard.stripe.com${company.billing?.customer?.livemode
                                ? ""
                                : "/test"}/customers/${company.billing?.customerId}"
                            ?disabled=${!company.billing}
                            target="_blank"
                        >
                            <ptc-ui-icon name="icon.object.total"></ptc-ui-icon>
                            In Stripe Öffnen
                        </a>
                        <a
                            class="small slim ghost button"
                            href="${PUBLIC_PTC_MANAGE_URL}/login?admin=1&amp;company=${company.id}"
                            target="_blank"
                        >
                            <ptc-ui-icon name="icon.navigation.forward"></ptc-ui-icon>
                            In Pentacode Öffnen
                        </a>
                    </div>

                    <ptc-scroller class="stretch collapse">
                        <div class="padded narrow-content">
                            <div class="double-padded border-top">
                                <div class="margined uppercase">
                                    Support Tools <ptc-ui-icon name="icon.object.support-tools"></ptc-ui-icon>
                                </div>

                                <div class="horizontal layout stretch">
                                    <div class="horizontally-margined">
                                        <button class="subtle" @click=${this._restoreDeletedTimeEntries}>
                                            <ptc-ui-icon name="icon.status.syncing"></ptc-ui-icon>
                                            Arbeitszeiten wiederherstellen
                                        </button>
                                    </div>
                                    <div class="horizontally-margined">
                                        <button class="subtle" @click=${this._unlockRevenueEntries}>
                                            <ptc-ui-icon name="icon.action.unlock"></ptc-ui-icon>
                                            Kassenbuch öffnen
                                        </button>
                                    </div>
                                    <div class="horizontally-margined">
                                        <button class="subtle" @click=${() => this._loginThrottleDialog.show({})}>
                                            <ptc-ui-icon name="icon.action.lock"></ptc-ui-icon>
                                            Anmeldesperre
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="double-padded border-top">
                                <div class="margined uppercase">Unternehmensdaten</div>
                                <div>${this._renderCompanyForm(company)}</div>
                            </div>

                            <div class="double-padded border-top">
                                <div class="margined uppercase">
                                    <strong>${company.venues.length}</strong>
                                    Standorte
                                </div>

                                <div class="smaller spacing grid" style="--grid-column-width: 16em">
                                    ${company.venues.map(
                                        (venue) => html`
                                            <div class="horizontal center-aligning layout double-padded box">
                                                <div class="stretch">${venue.name}</div>
                                            </div>
                                        `
                                    )}
                                </div>

                                <div class="margined uppercase">
                                    <strong>${company.archivedVenues.length}</strong>
                                    Archivierte Standorte
                                </div>
                                <div class="smaller spacing top-margined grid" style="--grid-column-width: 16em">
                                    ${company.archivedVenues.map(
                                        (venue) => html`
                                            <div class="horizontal center-aligning layout double-padded box">
                                                <div class="stretch">${venue.name}</div>
                                                <button
                                                    class="transparent icon"
                                                    title="Standort wiederherstellen"
                                                    @click=${() => this._restoreVenue(venue.id, venue.name)}
                                                >
                                                    <ptc-ui-icon name="icon.action.restore"></ptc-ui-icon>
                                                </button>
                                            </div>
                                        `
                                    )}
                                </div>
                            </div>

                            <div class="double-padded border-top">
                                ${this._importResult &&
                                html`
                                    <div
                                        class="margined ${this._importResult.status === "success"
                                            ? "green"
                                            : "red"} padded box spacing start-aligning vertical layout"
                                    >
                                        ${this._importResult?.status === "failure"
                                            ? html`
                                                  <div class="horizontal layout center-aligning">
                                                      <div class="stretch bold">
                                                          Achtung! Folgende Fehler müssen behoben werden, damit die
                                                          Mitarbeiter importiert werden können:
                                                      </div>
                                                  </div>
                                              `
                                            : html`
                                                  <div class="horizontal layout center-aligning">
                                                      <div class="stretch bold">Import erfolgreich!</div>
                                                  </div>
                                              `}
                                        ${this._importResult.errors.length > 0
                                            ? this._importResult.errors.map((message) => {
                                                  return html`
                                                      <div class="horizontal layout center-aligning">
                                                          ${this._importResult?.status === "success"
                                                              ? html`<ptc-ui-icon
                                                                    name="icon.status.verified"
                                                                    class="big right-margined"
                                                                ></ptc-ui-icon>`
                                                              : html`<ptc-ui-icon
                                                                    name="icon.status.warning"
                                                                    class="big right-margined"
                                                                ></ptc-ui-icon>`}
                                                          <div class="stretch">${message}</div>
                                                      </div>
                                                  `;
                                              })
                                            : ""}
                                    </div>
                                `}
                                <div
                                    class="margined between-justifying end-aligning layout uppercase"
                                    style="margin-right: 0; margin-top: 0;"
                                >
                                    <div>
                                        <strong>${activeEmployees.length}</strong>
                                        Mitarbeiter
                                    </div>

                                    <button class="small slim ghost" @click=${this._import}>
                                        <ptc-ui-icon name="icon.action.import"></ptc-ui-icon>
                                        Importieren
                                    </button>
                                </div>

                                <div class="spacing grid" style="--grid-column-width: 16em">
                                    ${employees.map(
                                        (e) => html`
                                            <div class="padded box spacing horizontal layout">
                                                <ptc-avatar .employee=${e} class="small"></ptc-avatar>
                                                <div class="stretch collapse">
                                                    <div class="horizontal center-aligning layout">
                                                        <div class="small stretch ellipsis collapse">${e.name}</div>
                                                        <a
                                                            id="employeePhone${e.id}"
                                                            href="tel:${e.phone}"
                                                            class="tiny slim transparent button"
                                                            ?disabled=${!e.phone}
                                                        >
                                                            <ptc-ui-icon name="icon.communication.phone"></ptc-ui-icon>
                                                        </a>
                                                        <ptc-ui-tooltip for="employeePhone${e.id}">
                                                            <div>${e.phone}</div>
                                                            ${e.phone2 ? html`<div>${e.phone2}</div>` : ""}
                                                        </ptc-ui-tooltip>
                                                        <a
                                                            id="employeeEmail${e.id}"
                                                            href="https://secure.helpscout.net/mailbox/a4f33595e56f4aff/new-ticket/?email=${encodeURIComponent(
                                                                e.email
                                                            )}"
                                                            target="_blank"
                                                            class="tiny slim transparent button"
                                                            ?disabled=${!e.email}
                                                        >
                                                            <ptc-ui-icon name="icon.action.send-message"></ptc-ui-icon>
                                                        </a>
                                                        <ptc-ui-tooltip for="employeeEmail${e.id}"
                                                            >${e.email}</ptc-ui-tooltip
                                                        >

                                                        <button id="employeeMenu${e.id}" class="tiny slim transparent">
                                                            <ptc-ui-icon name="icon.action.show-more"></ptc-ui-icon>
                                                        </button>
                                                        <ptc-ui-popover for="employeeMenu${e.id}" placement="bottom" data-popover="close">
                                                            <div class="vertical layout">
                                                                ${!e.isContactPerson
                                                                    ? html`
                                                                          <button
                                                                              class="transparent slim"
                                                                              @click=${() =>
                                                                                  this._setContactPerson(e, true)}
                                                                          >
                                                                              <ptc-ui-icon
                                                                                  name="icon.action.contact-support"
                                                                              ></ptc-ui-icon>
                                                                              Kontaktperson Hinzufügen
                                                                          </button>
                                                                      `
                                                                    : html`
                                                                          <button
                                                                              class="transparent slim"
                                                                              @click=${() =>
                                                                                  this._setContactPerson(e, false)}
                                                                          >
                                                                              <ptc-ui-icon
                                                                                  name="icon.action.remove-contact"
                                                                              ></ptc-ui-icon>
                                                                              Kontaktperson Entfernen
                                                                          </button>
                                                                      `}
                                                                ${e.role === Role.Owner
                                                                    ? html`
                                                                          <button
                                                                              class="transparent slim"
                                                                              @click=${() =>
                                                                                  this._resendOwnerWelcomeEmail(e)}
                                                                          >
                                                                              <ptc-ui-icon
                                                                                  name="icon.communication.email"
                                                                              ></ptc-ui-icon>
                                                                              Willkommens-E-Mail erneut senden
                                                                          </button>
                                                                      `
                                                                    : ""}
                                                            </div>
                                                        </ptc-ui-popover>
                                                    </div>
                                                    <div class="tiny pills">
                                                        ${e.role === Role.Owner
                                                            ? html`<div class="purple pill">
                                                                  <ptc-ui-icon name="icon.role.owner"></ptc-ui-icon>
                                                                  Besitzer
                                                              </div>`
                                                            : e.role === Role.Manager
                                                              ? html`<div class="orange pill">
                                                                    <ptc-ui-icon name="icon.role.manager"></ptc-ui-icon>
                                                                    Manager
                                                                </div>`
                                                              : html`<div class="pill">
                                                                    <ptc-ui-icon name="icon.role.staff"></ptc-ui-icon>
                                                                    Mitarbeiter
                                                                </div>`}
                                                        ${e.isContactPerson
                                                            ? html`
                                                                  <div class="blue pill">
                                                                      <ptc-ui-icon
                                                                          name="icon.action.contact-support"
                                                                      ></ptc-ui-icon>
                                                                      Kontaktperson
                                                                  </div>
                                                              `
                                                            : ""}
                                                        ${e.status !== EmployeeStatus.Active
                                                            ? html`
                                                                  <div class="${employeeStatusColor(e.status)} pill">
                                                                      ${employeeStatusLabel(e.status)}
                                                                  </div>
                                                              `
                                                            : ""}
                                                    </div>
                                                </div>
                                            </div>
                                        `
                                    )}
                                    ${!this._displayAllEmployees
                                        ? html`
                                              <button class="subtle" @click=${() => (this._displayAllEmployees = true)}>
                                                  Alle Anzeigen
                                              </button>
                                          `
                                        : html`
                                              <button
                                                  class="subtle"
                                                  @click=${() => (this._displayAllEmployees = false)}
                                              >
                                                  Weniger Anzeigen
                                              </button>
                                          `}
                                </div>
                            </div>

                            ${this._renderBillingForm()}

                            <div class="double-padded border-top">
                                <div class="margined uppercase">Zahlungsmittel</div>

                                ${this._billingInfo?.billingMode === BillingMode.Hogast
                                    ? html`
                                          <p
                                              class="margined ${company.billing?.paymentMethods?.length
                                                  ? "red colored-text"
                                                  : "subtle"}"
                                          >
                                              Für die Abrechnung über Hogast werden keine Zahlungsmittel benötigt.
                                              ${company.billing?.paymentMethods?.length
                                                  ? "Wenn der Kunde auf Hogast umgestellt werden soll, bitte die Zahlungsmittel entfernen."
                                                  : ""}
                                          </p>
                                      `
                                    : ""}
                                ${company.billing?.paymentMethods.map(
                                    (pm) => html`
                                        <div class="payment-method-container">
                                            <div>
                                                <div class="payment-method">
                                                    <div class="payment-method-detail">
                                                        <div class="payment-method-detail-label">Kontoinhaber:</div>
                                                        <div class="payment-method-detail-value">
                                                            ${pm.billing_details.name}
                                                        </div>
                                                    </div>

                                                    <div class="payment-method-detail">
                                                        <div class="payment-method-detail-label">IBAN:</div>
                                                        <div class="payment-method-detail-value">
                                                            ${pm.sepa_debit?.country} •••• •••• •••• ••••
                                                            ${pm.sepa_debit?.last4}
                                                        </div>
                                                    </div>

                                                    <div class="payment-method-detail">
                                                        <div class="payment-method-detail-label">Hinzugefügt:</div>
                                                        <div class="payment-method-detail-value">
                                                            ${formatDate(new Date(pm.created * 1000))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <div
                                                    class="pill"
                                                    ?hidden=${company.billing!.customer!.invoice_settings!
                                                        .default_payment_method !== pm.id}
                                                >
                                                    Standard
                                                </div>

                                                <button
                                                    class="slim transparent red"
                                                    @click=${() => this._deletePaymentMethod(pm.id)}
                                                >
                                                    <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                                                </button>
                                            </div>
                                        </div>
                                    `
                                )}
                                ${this._billingInfo?.billingMode !== BillingMode.Hogast
                                    ? html`
                                          <button @click=${() => this._addPaymentMethod(company)} class="subtle">
                                              <ptc-ui-icon name="icon.object.payment-method"></ptc-ui-icon>
                                              Zahlungsmittel Hinzufügen
                                          </button>

                                          <button
                                              ?hidden=${!company.billing}
                                              @click=${() => this._openStripeDashboard(company)}
                                              class="subtle"
                                          >
                                              <ptc-ui-icon name="icon.navigation.forward"></ptc-ui-icon>
                                              In Stripe Öffnen
                                          </button>
                                      `
                                    : ""}
                            </div>
                            <div class="double-padded border-top">
                                <div
                                    class="margined between-justifying end-aligning layout uppercase"
                                    style="margin-right: 0; margin-top: 0;"
                                >
                                    <div>Zahlungsverlauf</div>
                                    <button
                                        ?hidden=${PUBLIC_PTC_LOAD_INVOICES_ADMIN !== "true"}
                                        class="small slim ghost"
                                        @click=${() => this._invoicesTask.run([company.id, app.api, true])}
                                    >
                                        <ptc-ui-icon name="icon.action.refresh"></ptc-ui-icon>
                                        Rechnungen neu laden
                                    </button>
                                </div>

                                <div class="box">
                                    ${this._invoicesTask.render({
                                        initial: () =>
                                            html`<div class="double-padded text-centering">
                                                Laden der Rechnungen ist deaktiviert.
                                            </div>`,
                                        pending: () =>
                                            html`<div class="horizontal centering layout double-padded">
                                                <ptc-ui-spinner active style="font-size: 0.53em"></ptc-ui-spinner>
                                            </div>`,
                                        error: () =>
                                            html`<div class="double-padded text-centering">
                                                Rechnungen konnten nicht geladen werden!
                                            </div>`,
                                        complete: (invoices) => {
                                            if (!invoices.length) {
                                                return html`<div class="double-padded text-centering subtle">
                                                    Es liegen noch keine Rechnungen vor.
                                                </div>`;
                                            }

                                            return invoices.map(
                                                (invoice) => html`
                                                    <div
                                                        class="invoice horizontal center-aligning layout padded-medium pad-children click ${invoice.status}"
                                                        @click=${() => this._invoiceDialog.show(invoice)}
                                                    >
                                                        <ptc-ui-icon
                                                            name="${invoice.statusIcon}"
                                                            class="${invoice.statusColor}"
                                                        ></ptc-ui-icon>
                                                        <div class="invoice-date">
                                                            ${invoice.billingDate
                                                                ? formatDate(invoice.billingDate)
                                                                : formatDate(invoice.period.start)}
                                                        </div>
                                                        <div class="stretch ellipsis">
                                                            ${invoice.items.map((item) => item.description).join(", ")}
                                                        </div>
                                                        <div class="invoice-amount">
                                                            ${invoice.total !== invoice.adjustedTotal
                                                                ? html`
                                                                      <div class="subtle line-through">
                                                                          ${formatNumber(invoice.total)} €
                                                                      </div>
                                                                      <div>
                                                                          ${formatNumber(invoice.adjustedTotal)} €
                                                                      </div>
                                                                  `
                                                                : `${formatNumber(invoice.total)} €`}
                                                        </div>
                                                        ${invoice.status === InvoiceStatus.Draft
                                                            ? html`
                                                                  <button
                                                                      class="transparent icon red"
                                                                      @click=${async (event: MouseEvent) => {
                                                                          event.stopPropagation();
                                                                          await this._deleteDraftInvoice(invoice);
                                                                      }}
                                                                  >
                                                                      <ptc-ui-icon
                                                                          name="icon.action.delete"
                                                                      ></ptc-ui-icon>
                                                                  </button>
                                                              `
                                                            : ""}
                                                        ${invoice.raw.metadata?.sentOutToCustomer
                                                            ? html`
                                                                  <ptc-ui-icon
                                                                      id="invoiceSent${invoice.id}"
                                                                      name="icon.status.email-sent"
                                                                      class="green colored-text"
                                                                  ></ptc-ui-icon>
                                                                  <ptc-ui-tooltip for="invoiceSent${invoice.id}">
                                                                      Rechnung wurde per Email versendet.
                                                                  </ptc-ui-tooltip>
                                                              `
                                                            : html`
                                                                  <button
                                                                      id="invoiceNotSent${invoice.id}"
                                                                      class="transparent icon"
                                                                      @click=${async (event: MouseEvent) => {
                                                                          event.stopPropagation();
                                                                          await this._sendInvoice(invoice.id);
                                                                      }}
                                                                  >
                                                                      <ptc-ui-icon
                                                                          name="icon.status.warning"
                                                                          class="red colored-text"
                                                                      ></ptc-ui-icon>
                                                                  </button>
                                                                  <ptc-ui-tooltip for="invoiceNotSent${invoice.id}">
                                                                      Rechnung wurde nicht per Email versendet. Zum
                                                                      Versand hier klicken.
                                                                  </ptc-ui-tooltip>
                                                              `}
                                                    </div>
                                                `
                                            );
                                        },
                                    })}
                                </div>
                            </div>
                        </div>
                    </ptc-scroller>
                </div>
                <div class="padded vertical layout border-left" style="width: 25em">
                    <form class="new-event-form" @submit=${this._submitNewEvent}>
                        <div class="relative">
                            <ptc-textarea
                                name="comment"
                                @input=${() => this.requestUpdate()}
                                disableEmojis
                                placeholder="Neuer Kommentar..."
                            ></ptc-textarea>
                            <div
                                class="smaller padded bottom horizontal center-aligning spacing layout fill-horizontally absolute"
                            >
                                <select class="skinny" name="status" @change=${() => this.requestUpdate()}>
                                    <option .value=${""}>[Neuer Status]</option>
                                    <option .value=${CompanyStatus.TrialWithoutContract}>
                                        ${companyStatusLabel(CompanyStatus.TrialWithoutContract)}
                                    </option>
                                    <option .value=${CompanyStatus.Trialing}>
                                        ${companyStatusLabel(CompanyStatus.Trialing)}
                                    </option>
                                    <option .value=${CompanyStatus.Probation}>
                                        ${companyStatusLabel(CompanyStatus.Probation)}
                                    </option>
                                    <option .value=${CompanyStatus.Active}>
                                        ${companyStatusLabel(CompanyStatus.Active)}
                                    </option>
                                    <option .value=${CompanyStatus.Paused}>
                                        ${companyStatusLabel(CompanyStatus.Paused)}
                                    </option>
                                    <option .value=${CompanyStatus.Canceled}>
                                        ${companyStatusLabel(CompanyStatus.Canceled)}
                                    </option>
                                    <option .value=${CompanyStatus.PaymentOverdue}>
                                        ${companyStatusLabel(CompanyStatus.PaymentOverdue)}
                                    </option>
                                </select>
                                <div class="horizontal center-aligning layout" ?hidden=${!this._newEventData.status}>
                                    <label>Ab:</label>
                                    <ptc-date-input
                                        name="date"
                                        datePicker="popover"
                                        style="--date-input-padding: 0.3em"
                                    ></ptc-date-input>
                                </div>
                                <div class="stretch"></div>
                                <button
                                    class="slim primary"
                                    ?disabled=${!this._newEventData.comment && !this._newEventData.status}
                                >
                                    Absenden
                                </button>
                            </div>
                        </div>
                    </form>

                    <ptc-scroller class="stretch collapse" id="timeLineScroller">
                        ${this._renderLifeCycleEvents()}
                        <div style="height: 30%;"></div>
                    </ptc-scroller>
                </div>
            </div>

            <div class="fullbleed centering layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner .active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
